'use client';

import { useEffect, useState } from 'react';

import { useBaseAuth } from '@cloudquery/base-users';
import { useLocation } from 'react-router';
import {
  trackEvent,
  setRudderstackCurrentTeam,
  trackRudderstackPageView,
  identifyRudderstackUser,
  initRudderstack,
  groupRudderstackUser,
  anonymizeRudderstackUser,
} from 'shared/base';

import { useTrackingConsent } from '@/context/trackingConsent';

import isNonProductionEnvironment from '../isNonProductionEnvironment';

const PRODUCTION_FALLBACK_WRITE_KEY = '2tdE43UtwcKCukcasR4x5glOG94';
const PRODUCTION_FALLBACK_DATAPLANE_URL = 'https://analytics-events.cloudquery.io';
const RESERVED_INTERNAL_ADMIN_EMAIL = 'admin@localhost';

const getFallbackWriteKey = () =>
  isNonProductionEnvironment() ? undefined : PRODUCTION_FALLBACK_WRITE_KEY;

const getFallbackDataplaneUrl = () =>
  isNonProductionEnvironment() ? undefined : PRODUCTION_FALLBACK_DATAPLANE_URL;

export function Tracking() {
  const { currentMembership, loggedInRef, user } = useBaseAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const { pathname } = useLocation();

  const { trackingOptedIn } = useTrackingConsent();

  useEffect(() => {
    if (!trackingOptedIn) {
      anonymizeRudderstackUser();

      return;
    }

    initRudderstack(getFallbackWriteKey(), getFallbackDataplaneUrl()).finally(() => {
      setIsInitialized(true);
    });
  }, [trackingOptedIn]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (
      user?.event_identifiers &&
      user.registered_team_name &&
      typeof user.event_identifiers.installation_id === 'string'
    ) {
      identifyRudderstackUser(user.id, {
        email:
          user.email === RESERVED_INTERNAL_ADMIN_EMAIL
            ? `${user.email.split('@')[0]}@${currentMembership?.team.name}`
            : user.email,
        name: user.name,
      });

      groupRudderstackUser(user.registered_team_name, {
        groupType: 'team',
        installation_id: user.event_identifiers.installation_id,
        team_name: user.registered_team_name,
      });

      if (loggedInRef.current) {
        loggedInRef.current = false;
        trackEvent('user_logged_in');
      }
    } else {
      anonymizeRudderstackUser();
      setRudderstackCurrentTeam(undefined);
    }
  }, [isInitialized, currentMembership?.team.name, user, loggedInRef]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    setRudderstackCurrentTeam(
      user && user.registered_team_name
        ? {
            internal: user.registered_team_internal ?? false,
            name: user.registered_team_name,
          }
        : undefined,
    );
  }, [isInitialized, user]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (pathname) {
      trackRudderstackPageView();
    }
  }, [isInitialized, pathname]);

  return null;
}
